import React from "react";
import { useTheme } from "styled-components";

import { useRovrDuotoneFilter } from "./useRovrDuotoneFilter";
import { DuotoneImg, DuotoneWrapper } from "./RovrDuotoneImage.styles";
import type { RovrDuotoneImageProps, RovrDuotoneIntensity } from "./types";

const INTENSITY_MAP: Record<
  RovrDuotoneIntensity,
  {
    brightness: number;
    contrast: number;
    saturate: number;
    overlayOpacity: number;
  }
> = {
  soft: {
    brightness: 0.9,
    contrast: 1.05,
    saturate: 0.8,
    overlayOpacity: 0.15,
  },
  normal: {
    brightness: 0.85,
    contrast: 1.1,
    saturate: 0.9,
    overlayOpacity: 0.2,
  },
  hard: { brightness: 0.75, contrast: 1.2, saturate: 1, overlayOpacity: 0.3 },
};

const INTENSITY_GAMMA: Record<RovrDuotoneIntensity, number> = {
  soft: 0.9,
  normal: 1,
  hard: 1.15,
};

const RovrDuotoneImage: React.FC<RovrDuotoneImageProps> = ({
  src,
  alt,
  variant = "blend",
  intensity = "normal",
  className,
  style,
  width,
  height,
  objectFit = "cover",
  loading = "lazy",
  decoding = "async",
}) => {
  const theme = useTheme();
  const yellow = theme?.colors?.primary ?? "#FFF057";
  const black = theme?.colors?.black ?? "#000000";

  const intensityValues = INTENSITY_MAP[intensity];
  const blendFilter = `grayscale(1) brightness(${intensityValues.brightness}) contrast(${intensityValues.contrast}) saturate(${intensityValues.saturate})`;

  const { defs, style: filterStyle } = useRovrDuotoneFilter({
    dark: black,
    light: yellow,
    gamma: INTENSITY_GAMMA[intensity],
  });

  const isDecorative = alt.trim() === "";

  const imgProps = {
    src,
    alt: isDecorative ? "" : alt,
    loading,
    decoding,
    "aria-hidden": isDecorative ? true : undefined,
  };

  if (variant === "blend") {
    return (
      <DuotoneWrapper
        className={className}
        style={style}
        $objectFit={objectFit}
        $width={width}
        $height={height}
        $backgroundColor={yellow}
      >
        <DuotoneImg
          {...imgProps}
          $objectFit={objectFit}
          $mixBlendMode="multiply"
          $filter={blendFilter}
        />
      </DuotoneWrapper>
    );
  }

  return (
    <DuotoneWrapper
      className={className}
      style={style}
      $objectFit={objectFit}
      $width={width}
      $height={height}
      $backgroundColor={black}
    >
      {defs}
      <DuotoneImg {...imgProps} $objectFit={objectFit} style={filterStyle} />
    </DuotoneWrapper>
  );
};

export default RovrDuotoneImage;
